import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { getStaleChunkReloadToken, isStaleChunkError } from "./error-boundary-utils";

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

// Session-storage key used to prevent infinite reload loops.
const STALE_CHUNK_RELOAD_KEY = "stale_chunk_reloaded";

class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        errorInfo: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error, errorInfo: null };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Uncaught error:", error, errorInfo);
        this.setState({ errorInfo });

        // Auto-reload once when stale chunks are detected after a new deployment.
        // The session-storage token prevents an infinite reload loop for the
        // same broken chunk while still allowing recovery on later deployments.
        const staleChunkReloadToken = getStaleChunkReloadToken(error);
        if (staleChunkReloadToken) {
            const previousToken = sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY);
            if (previousToken !== staleChunkReloadToken) {
                sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, staleChunkReloadToken);
                window.location.reload();
            }
        }
    }

    public render() {
        if (this.state.hasError) {
            const isStale = this.state.error ? isStaleChunkError(this.state.error) : false;

            if (isStale) {
                // Show a brief loading message while the reload is in progress.
                return (
                    <div className="flex items-center justify-center min-h-screen bg-white dark:bg-zinc-900">
                        <p className="text-gray-500 dark:text-gray-400 text-sm">Loading the latest version of this page...</p>
                    </div>
                );
            }

            return (
                <div className="p-8 bg-red-50 text-red-900 min-h-screen">
                    <h1 className="text-2xl font-bold mb-4">Something went wrong.</h1>
                    <p className="mb-4 text-sm">Try refreshing the page. If the problem persists, please contact us.</p>
                    <button onClick={() => window.location.reload()} className="px-4 py-2 bg-red-700 text-white rounded text-sm mb-6">
                        Refresh page
                    </button>
                    <details className="bg-white p-4 rounded shadow overflow-auto">
                        <summary className="cursor-pointer text-sm font-medium mb-2">Error details</summary>
                        <p className="font-mono text-sm mb-2">{this.state.error?.toString()}</p>
                        <pre className="font-mono text-xs text-gray-600">{this.state.errorInfo?.componentStack}</pre>
                    </details>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
